import { API_BASE_URL } from "@/lib/axios";
import axios from "axios";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getSecureItem,
  clearSecureStorage,
  getSecureUser,
  setSecureUser,
  getAuthToken,
  getLoginType,
  setLoginType
} from "@/utils/secureStorage";

export type UserRole =
  | "employee"
  | "department_admin"
  | "company_admin"

export interface RegisterUserData {
  fullName: string;
  email: string;
  phoneNumber: string;
  password: string;
  passwordConfirm: string;
  role?: UserRole;
  companyId: string;
  departmentId: string;
  photo?: string;
  isVerified?: boolean;
  isActive?: boolean;
  failedLoginAttempts?: number;
  preferences?: {
    theme: "light" | "dark";
    language: "am" | "en" | "om" | "tg";
  };
}

export interface RegisterUserWithTokenData {
  fullName: string;
  phoneNumber: string;
  password: string;
  passwordConfirm: string;
  preferences?: {
    theme: "light" | "dark";
    language: "am" | "en" | "om" | "tg";
  };
}

export interface User {
  _id: string;
  id?: string;
  fullName: string;
  name?: string;
  email: string;
  primaryEmail?: string;
  secondaryEmail?: string;
  phoneNumber: string;
  role: UserRole;
  companyId: string;
  departmentId: string;
  departments?: Array<{ _id: string; name: string }>;
  photo: string;
  isVerified: boolean;
  isActive: boolean;
  failedLoginAttempts: number;
  failedLoginAttemptsMade?: number;
  mfaEnabled?: boolean;
  mfaBy?: string;
  subscriptionPlan?: "standard" | "enterprise" | "free";
  preferences: {
    theme: "light" | "dark";
    language: "am" | "en" | "om" | "tg";
  };
  createdAt?: string;
  updatedAt?: string;
  language?: string;
  lastLogin: string;
  learningProgress?: Array<{
    courseId: string;
    progress: number;
    isCompleted: boolean;
    isInProgress: boolean;
    startedAt?: string;
    completedAt?: string;
    quizzes?: Array<{ score: number; [key: string]: any }>;
    tutorials?: Array<{
      tutorialId: string;
      progress: number;
      isCompleted: boolean;
      isInProgress: boolean;
      lastWatchedAt?: string;
    }>;
  }>;
}


export interface CompanyRegistrationData {
  name: string;
  primaryEmail: string;
  secondaryEmail?: string;
  phoneNumber: string;
  password: string;
  passwordConfirm: string;
}

interface LoginResult {
  requiresOtp: boolean;
  userId?: string;
  user?: User;
}

interface AuthContextType {
  currentUser: User | null;
  currentUserLoading: boolean;
  isInitialized: boolean;
  isInitializing: boolean;
  user: User | null;
  isAuthenticated: boolean;
  updateAuthState: (user: User) => void;
  refreshUserData: () => Promise<void>;
  clearAuthState: () => void;
  loginUser: (
    email: string,
    password: string,
    isCompany?: boolean
  ) => Promise<LoginResult>;
  logoutUser: () => Promise<void>;
  registerCompany: (data: CompanyRegistrationData) => Promise<void>;
  error: string | null;
  isLoading: boolean;
}

// List of public routes that don't require authentication
const PUBLIC_ROUTES = [
  "/",
  "/login",
  "/register",
  "/register-company",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/verify-otp",
];

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

// Move clearAuthState to top-level
const clearAuthState = () => {
  console.log("Clearing auth state");
  // Clear secure storage items
  clearSecureStorage();

  // Clear all cookies by setting them to expire in the past
  const clearAllCookies = () => {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
    }
  };
  clearAllCookies();
  // Clear sessionStorage
  sessionStorage.clear();
  // Clear axios default headers
  delete axios.defaults.headers.common["Authorization"];
  console.log("Auth state cleared completely");
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentUserLoading, setCurrentUserLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const initializationRef = useRef(false);

  // Check if current route is public - memoized to prevent unnecessary re-renders
  const isPublicRoute = useMemo(() => {
    return PUBLIC_ROUTES.includes(location.pathname);
  }, [location.pathname]);

  // Memoize error messages to prevent unnecessary re-renders
  const loginFailedMessage = useMemo(() => t("auth.loginFailed"), [t]);
  const registrationFailedMessage = useMemo(
    () => t("auth.registrationFailed"),
    [t]
  );

  // Initialize auth state
  useEffect(() => {
    // Prevent re-initialization if already initialized
    if (initializationRef.current) {
      return;
    }

    const initializeAuth = async () => {
      const token = getAuthToken();
      const loginType = getLoginType();

      // Always try to use stored data first, regardless of token
      const storedUser = getSecureUser();
      const storedCompany = getSecureItem("company");
      
      if (storedUser) {
        try {
          const userData = storedUser;
          console.log("Using stored user data:", userData);
          setCurrentUser(userData);
          setCurrentUserLoading(false);
          setIsInitialized(true);
          initializationRef.current = true;
          return;
        } catch (parseError) {
          console.log("Failed to parse stored user data");
        }
      }
      
      if (storedCompany) {
        try {
          const companyData = JSON.parse(storedCompany);
          console.log("Using stored company data:", companyData);
          const userData = {
            ...companyData,
            role: "company_admin",
            fullName: companyData.name,
            companyId: companyData._id,
            departmentId: "",
            photo: "",
            isActive: true,
            isVerified: true,
            lastLogin: new Date().toISOString(),
          } as User;
          setCurrentUser(userData);
          setCurrentUserLoading(false);
          setIsInitialized(true);
          initializationRef.current = true;
          return;
        } catch (parseError) {
          console.log("Failed to parse stored company data");
        }
      }

      if (token) {
        try {
          setCurrentUserLoading(true);
          // Set the token in axios headers
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          console.log("Initializing auth with login type:", loginType);

          // First try to get company data
          try {
            const companyResponse = await axios.get(
              `${API_BASE_URL}/api/v1/companies/get-current-company`,
              {
                withCredentials: true,
              }
            );

            if (
              companyResponse.data.status === "success" &&
              companyResponse.data.data?.document
            ) {
              // If we have company data, update auth state with it
              const companyData = companyResponse.data.data.document;
              const userData = {
                ...companyData,
                role: "company_admin",
                fullName: companyData.name,
                companyId: companyData._id,
                departmentId: "",
                photo: "",
                isActive: true,
                isVerified: true,
                lastLogin: new Date().toISOString(),
              } as User;
              console.log("Setting company user data:", userData);
              setCurrentUser(userData);
              setLoginType("company");
              setCurrentUserLoading(false);
              setIsInitialized(true);
              initializationRef.current = true;
              return;
            }
          } catch (companyErr: any) {
            console.log(
              "Company endpoint failed:",
              companyErr.response?.status
            );
            // If company endpoint fails with 401, log out the user
            if (companyErr.response?.status === 401) {
              clearAuthState();
              window.location.href = "/login";
              return;
            }
            // If company endpoint fails with other error, try user endpoint
            if (companyErr.response?.status !== 401) {
              throw companyErr;
            }
          }

          // If no company data and login type is company, try user endpoint as fallback
          if (loginType === "company") {
            console.log(
              "No company data found, trying user endpoint as fallback"
            );
            try {
              const userResponse = await axios.get(
                `${API_BASE_URL}/api/v1/users/update-me`,
                {
                  withCredentials: true,
                }
              );

              if (
                userResponse.data.status === "success" &&
                userResponse.data.data?.document
              ) {
                const userData = userResponse.data.data.document;
                console.log("Setting user data from fallback:", userData);
                setCurrentUser(userData);
                setLoginType("user");
                setCurrentUserLoading(false);
                setIsInitialized(true);
                initializationRef.current = true;
                return;
              }
            } catch (userErr: any) {
              console.log(
                "User endpoint fallback failed:",
                userErr.response?.status
              );
              if (userErr.response?.status === 401) {
                clearAuthState();
                window.location.href = "/login";
                return;
              }
            }
          }

          // If login type is user or no login type, try user endpoint
          if (loginType === "user" || !loginType) {
            console.log("Trying user endpoint for user login");

            // For user login, we should have the user data from the login response
            // stored in secure storage. Let's check if we have it there.
            const storedUser = getSecureUser();
            if (storedUser) {
              try {
                const userData = storedUser;
                console.log("Setting user data from secure storage:", userData);
                setCurrentUser(userData);
                setLoginType("user");
                setCurrentUserLoading(false);
                setIsInitialized(true);
                initializationRef.current = true;
                return;
              } catch (parseError) {
                console.log("Failed to parse stored user data");
              }
            }

            // If no stored user data, try to get from API as fallback
            try {
              const userResponse = await axios.get(
                `${API_BASE_URL}/api/v1/users/get-current-user`,
                {
                  withCredentials: true,
                }
              );

              if (
                userResponse.data.status === "success" &&
                userResponse.data.data?.document
              ) {
                const userData = userResponse.data.data.document;
                console.log("Setting user data from API fallback:", userData);
                setCurrentUser(userData);
                setSecureUser(userData);
                setLoginType("user");
                setCurrentUserLoading(false);
                setIsInitialized(true);
                initializationRef.current = true;
                return;
              }
            } catch (userErr: any) {
              console.log(
                "User API fallback failed:",
                userErr.response?.status
              );
              if (userErr.response?.status === 401) {
                clearAuthState();
                window.location.href = "/login";
                return;
              }
            }

            // Only clear auth state if we have no stored data AND no API data
            console.log(
              "No user data found from any source, clearing auth state"
            );
            // Don't clear auth state, just set loading to false
            console.log("Keeping auth state even with no data");
            setCurrentUserLoading(false);
            setIsInitialized(true);
            initializationRef.current = true;
            return;
          }
        } catch (err: any) {
          console.error("Error initializing auth:", err);
          // Try to use stored data as fallback before clearing auth state
          const storedUser = getSecureUser();
          const storedCompany = getSecureItem("company");
          
          if (storedUser) {
            try {
              const userData = storedUser;
              console.log("Using stored user data as fallback:", userData);
              setCurrentUser(userData);
              setCurrentUserLoading(false);
              setIsInitialized(true);
              initializationRef.current = true;
              return;
            } catch (parseError) {
              console.log("Failed to parse stored user data on error");
            }
          }
          
          if (storedCompany) {
            try {
              const companyData = JSON.parse(storedCompany);
              console.log("Using stored company data as fallback:", companyData);
              const userData = {
                ...companyData,
                role: "company_admin",
                fullName: companyData.name,
                companyId: companyData._id,
                departmentId: "",
                photo: "",
                isActive: true,
                isVerified: true,
                lastLogin: new Date().toISOString(),
              } as User;
              setCurrentUser(userData);
              setCurrentUserLoading(false);
              setIsInitialized(true);
              initializationRef.current = true;
              return;
            } catch (parseError) {
              console.log("Failed to parse stored company data on error");
            }
          }
          
          // Only clear auth state if we have no stored data
          if (!storedUser && !storedCompany) {
            console.log("No stored data available, clearing auth state");
            // Don't clear auth state, just set loading to false
            console.log("Keeping auth state even with no stored data");
            setCurrentUserLoading(false);
            setIsInitialized(true);
          } else {
            console.log("Using stored data as fallback, keeping auth state");
            setCurrentUserLoading(false);
            setIsInitialized(true);
          }
          initializationRef.current = true;
        }
      } else {
        // If no token and no stored data, just complete initialization without user
        console.log("No token found, completing initialization without user");
        setCurrentUserLoading(false);
        setIsInitialized(true);
        initializationRef.current = true;
      }
    };

    initializeAuth();
  }, []); // Empty dependency array since we use ref to prevent re-runs

  // Handle redirects for authenticated users on public routes
  useEffect(() => {
    console.log("Redirect useEffect triggered:", {
      currentUser: !!currentUser,
      isPublicRoute,
      isInitialized,
      currentUserLoading,
      userRole: currentUser?.role,
    });

    if (currentUser && isPublicRoute && isInitialized && !currentUserLoading) {
      console.log("Authenticated user on public route, redirecting");
      if (currentUser.role === "company_admin") {
        navigate("/admin/company", { replace: true });
      } else if (currentUser.role === "employee") {
        navigate("/employee/dashboard", { replace: true });
      } else if (currentUser.role === "department_admin") {
        navigate("/department-admin/dashboard", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [currentUser, isPublicRoute, isInitialized, currentUserLoading]);

  const updateAuthState = (user: User) => {
    console.log("Updating auth state with user:", user);

    // Check login type first
    const loginType = getLoginType();
    console.log("Login type:", loginType);
    console.log("User role from backend:", user.role);

    // Determine role based on login type and backend response
    let role: UserRole;

    if (loginType === "company") {
      // For company login, always set as company_admin
      role = "company_admin";
      console.log("Company login detected, setting role to company_admin");
    } else {
      // For user login, normalize the role from backend
      const backendRole = user.role || "employee";

      // Map backend role format to frontend format
      switch (backendRole as string) {
        case "departmentAdmin":
          role = "department_admin";
          break;
        case "companyAdmin":
          role = "company_admin";
          break;
        default:
          role = backendRole as UserRole;
      }

      console.log(
        "User login detected, normalized role from backend:",
        backendRole,
        "to:",
        role
      );
    }

    console.log("Final determined role:", role);

    const userWithRole: User = {
      ...user,
      role,
    };

    // Store user data in secure storage for persistence
    setSecureUser(userWithRole);

    setCurrentUser(userWithRole);
    setCurrentUserLoading(false);
    setIsInitialized(true);
  };

  const loginUser = async (
    email: string,
    password: string,
    isCompany: boolean = false
  ): Promise<LoginResult> => {
    try {
      setIsLoading(true);
      setError(null);
      console.log("Attempting login...", { email, isCompany });

      // Store login type
      setLoginType(isCompany ? "company" : "user");
      console.log("Stored login type:", isCompany ? "company" : "user");

      const requestBody = isCompany
        ? { primaryEmail: email, password }
        : { email, password };

      console.log("Login request body:", requestBody);

      const response = await axios.post(
        `${API_BASE_URL}/api/v1/authentication/${
          isCompany ? "company" : "user"
        }/login`,
        requestBody,
        { withCredentials: true }
      );

      console.log("Login response:", response.data);

      if (
        response.data.status === "success" &&
        response.data.message?.includes("OTP sent")
      ) {
        const userId = response.data.data?.id || response.data.data?._id;
        return {
          requiresOtp: true,
          userId,
          user: undefined,
        };
      }

      if (response.data.data?.document) {
        updateAuthState(response.data.data.document);
        return { requiresOtp: false, user: response.data.data.document };
      }

      throw new Error(loginFailedMessage);
    } catch (err: any) {
      console.error("Login error:", err);
      const errorMessage = err.response?.data?.message || loginFailedMessage;
      setError(errorMessage);
      // Preserve the original error structure so the Login component can access it
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logoutUser = async () => {
    // Clear all secure storage
    clearSecureStorage();
    
    // Clear all cookies
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    }
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Clear axios headers
    delete axios.defaults.headers.common["Authorization"];
    
    // Clear auth state completely
    setCurrentUser(null);
    setCurrentUserLoading(false);
    setIsInitialized(false);
    initializationRef.current = false;
    
    // Redirect to home page
    window.location.href = "/";
  };

  const registerCompany = async (data: CompanyRegistrationData) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log("Registering company:", data);

      // Transform data to match API expectations, including required fields
      const apiData = {
        name: data.name,
        primaryEmail: data.primaryEmail,
        secondaryEmail: data.secondaryEmail,
        password: data.password,
        passwordConfirm: data.passwordConfirm,
        phoneNumber: data.phoneNumber,
      };

      console.log("API data being sent:", apiData);

      const response = await axios.post(
        `${API_BASE_URL}/api/v1/authentication/company/signup`,
        apiData,
        { withCredentials: true }
      );

      console.log("Company registration response:", response.data);

      if (response.data.status === "success") {
        // Registration successful, no need to set auth state yet
        // User will need to verify email first
        console.log("Company registration successful");
      } else {
        throw new Error(response.data.message || registrationFailedMessage);
      }
    } catch (err: any) {
      console.error("Company registration error:", err);
      console.error("Error response data:", err.response?.data);
      console.error("Error response status:", err.response?.status);
      console.error("Error response headers:", err.response?.headers);

      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        registrationFailedMessage;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUserData = async () => {
    try {
      console.log("Refreshing user data...");
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/users/get-current-user`,
        { withCredentials: true }
      );
      
      if (response.data.status === "success" && response.data.data?.document) {
        const userData = response.data.data.document;
        console.log("Refreshed user data:", userData);
        updateAuthState(userData);
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
    }
  };

  const value = {
    currentUser,
    currentUserLoading,
    isInitialized,
    isInitializing: currentUserLoading,
    user: currentUser,
    isAuthenticated: !!currentUser,
    updateAuthState,
    refreshUserData,
    clearAuthState,
    loginUser,
    logoutUser,
    registerCompany,
    error,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { clearAuthState };
